import React, { useState } from 'react';
import { Col, Form, Row, Switch } from 'antd';
import AppSelect from '@nxsys/components/AppSelect';
import Inheritable from '@nxsys/components/Inheritable';
import { TableOutlined } from '@ant-design/icons';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { defaultMarginMethod } from '@nxsys/utils';
import {
  AgencyTimesheetValueAccessEnum,
  enumMarginMethod,
} from '@nxsys/constants/AppEnums';
import { some } from 'lodash';
import MarginTableModal, { type MarginTableEntry } from '../MarginTableModal';
import type { PayrollFinanceSectionProps } from './types';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

const MarginSection: React.FC<PayrollFinanceSectionProps> = ({
  form,
  formMode,
  watchValues,
  visibility,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const [openMarginTableModal, setOpenMarginTableModal] = useState(false);
  const toggleMarginTable = () => setOpenMarginTableModal((v) => !v);
  const [localMarginData, setLocalMarginData] = useState<MarginTableEntry[]>(
    [],
  );
  const isCreateMode = formMode === 'create';

  const handleLocalDataChange = (data: MarginTableEntry[]) => {
    setLocalMarginData(data);
    form.setFieldValue(['margin', 'marginTableEntries'], data);
  };

  if (!visibility.margin) return null;

  return (
    <div className="pt-6">
      <div className="mb-6 text-xl font-medium text-gray-800">
        {messages['common.margin']}
      </div>
      <Form.Item
        label={messages['common.marginMethod']}
        name={['margin', 'marginMethod']}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <div className="flex gap-[8px]">
          <AppSelect
            options={defaultMarginMethod}
            placeholder={messages['form.select']}
            value={watchValues?.margin?.marginMethod?.value}
            onChange={(value) => {
              form.setFieldValue(['margin', 'marginMethod'], value);
            }}
          />
          {(watchValues?.margin?.marginMethod?.value ===
            enumMarginMethod.PERCENT_TABLE_ON_TIMESHEET_VALUE ||
            watchValues?.margin?.marginMethod?.value ===
              enumMarginMethod.FIXED_FEE_TABLE) && (
            <div className="flex items-center">
              <TableOutlined
                className="cursor-pointer"
                onClick={toggleMarginTable}
              />
            </div>
          )}
        </div>
      </Form.Item>
      <Row gutter={24}>
        <MarginTableModal
          title={
            defaultMarginMethod.find(
              (item) => item.value === watchValues.margin?.marginMethod?.value,
            )?.label ?? ''
          }
          isOpen={openMarginTableModal}
          toggle={toggleMarginTable}
          accessType={
            watchValues.margin?.marginMethod?.value ===
            enumMarginMethod.PERCENT_TABLE_ON_TIMESHEET_VALUE
              ? AgencyTimesheetValueAccessEnum.PERCENT
              : AgencyTimesheetValueAccessEnum.AMOUNT
          }
          mode={isCreateMode ? 'create' : 'update'}
          localData={localMarginData}
          onLocalDataChange={handleLocalDataChange}
        />
        <Col span={12}>
          <Form.Item
            label={messages['common.margin']}
            name={['margin', 'margin']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            {watchValues.margin?.marginMethod?.value ===
            enumMarginMethod.FIXED_FEE ? (
              <AppCurrencyInput placeholder={messages['form.enter']} />
            ) : (
              <AppRateInput
                placeholder={messages['form.enter']}
                disabled={some(
                  [
                    enumMarginMethod.FIXED_FEE_TABLE,
                    enumMarginMethod.PERCENT_TABLE_ON_TIMESHEET_VALUE,
                  ],
                  (item) => item === watchValues.margin?.marginMethod?.value,
                )}
              />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={messages['common.marginCapped']}
            name={['margin', 'marginCapped']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppCurrencyInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
      </Row>
      {watchValues?.margin?.marginMethod?.value ===
        enumMarginMethod.PERCENT_ON_TIMESHEET_VALUE && (
        <Form.Item
          label={messages['common.miniumMargin']}
          name={['margin', 'minMargin']}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <AppRateInput placeholder={messages['form.enter']} />
        </Form.Item>
      )}
      {/* NX-5228: the auto-reduce loop sits inside
        `payrollCountry !== IRELAND` in generateDraftPayrollJob, so this toggle
        saves a value the Irish calc never reads. Ireland was decided as
        error-only for an NMW breach — no margin auto-adjust in v1. */}
      {!isIreland && (
        <Form.Item
          name={['margin', 'autoReduceMarginForNMW']}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Inheritable<boolean>
            label={messages['common.autoReduceMarginForNMW'] as string}
            name={['margin', 'autoReduceMarginForNMW']}
            value={watchValues?.margin?.autoReduceMarginForNMW}
            inheritedValue={false}
            form={form}
          >
            {({ value, onChange, disabled }) => (
              <Switch
                checked={value ?? false}
                disabled={disabled}
                onChange={onChange}
              />
            )}
          </Inheritable>
        </Form.Item>
      )}
    </div>
  );
};

export default MarginSection;
